import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Flag, AlertTriangle, Info } from 'lucide-react';

function D9_SpecialConsiderations() {
  const { dataE, updateDataRaw, nextStep, previousStep } = useWizard();
  const special = dataE.specialConsiderations;

  const updateSpecial = (field, value) => {
    updateDataRaw({
      'wizardE.specialConsiderations': { ...special, [field]: value }
    });
  };

  const considerations = [
    {
      id: 'involvesMinors',
      label: 'Research involving children or young people (under 18)',
      detailsField: 'minorsDetails',
      helpText: 'Include information about parental consent, age-appropriate information, and safeguarding measures.',
      warning: true
    },
    {
      id: 'involvesHumanTissue',
      label: 'Research involving human tissue or biological samples',
      detailsField: 'tissueDetails',
      helpText: 'Include details about consent for tissue use, storage, and disposal procedures.',
      warning: true
    },
    {
      id: 'involvesClinical',
      label: 'Clinical trials or medical interventions',
      detailsField: 'clinicalDetails',
      helpText: 'Clinical trials may require additional regulatory approval beyond ethics committee review.',
      warning: true
    },
    {
      id: 'involvesIncentives',
      label: 'Participant incentives or payments',
      detailsField: 'incentivesDetails',
      helpText: 'Describe the type and value of incentives, and how you will avoid undue inducement.',
      warning: false
    },
    {
      id: 'involvesInternationalData',
      label: 'International data transfer',
      detailsField: 'internationalDetails',
      helpText: 'Describe which countries data will be transferred to and the legal basis for transfer.',
      warning: false
    },
    {
      id: 'securitySensitive',
      label: 'Security-sensitive research',
      detailsField: 'securityDetails',
      helpText: 'Research that could be misused for terrorism or other malicious purposes requires additional review.',
      warning: true
    }
  ];

  const hasAnySpecialConsiderations = considerations.some(c => special[c.id]);

  return (
    <div className="wizard-step">
      <div className="step-header">
        <Flag className="step-icon" size={32} />
        <div>
          <h2>Step 9: Special Considerations</h2>
          <p className="step-description">
            Indicate if any special circumstances apply to your research that may require additional review.
          </p>
        </div>
      </div>

      <div className="info-box info">
        <Info size={20} />
        <div>
          <strong>Additional Review Requirements</strong>
          <p>
            Some types of research require additional regulatory approval or specialized ethical review. 
            Please indicate if any of the following apply to your study.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Special Circumstances</h3>

        {considerations.map(consideration => (
          <div key={consideration.id} className="special-consideration-item">
            <div className="consideration-toggle">
              <label className="checkbox-option prominent">
                <input
                  type="checkbox"
                  checked={special[consideration.id] || false}
                  onChange={(e) => updateSpecial(consideration.id, e.target.checked)}
                />
                <span>{consideration.label}</span>
              </label>
            </div>

            {special[consideration.id] && (
              <div className="consideration-details">
                {consideration.warning && (
                  <div className="info-box warning small">
                    <AlertTriangle size={16} />
                    <span>This may require additional review or regulatory approval.</span>
                  </div>
                )}
                <div className="form-group">
                  <label>Please provide details:</label>
                  <textarea
                    value={special[consideration.detailsField] || ''}
                    onChange={(e) => updateSpecial(consideration.detailsField, e.target.value)}
                    placeholder={consideration.helpText}
                    rows={4}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!hasAnySpecialConsiderations && (
        <div className="info-box success">
          <Info size={20} />
          <p>
            No special considerations selected. If none of these apply to your research, 
            you can proceed to the next step.
          </p>
        </div>
      )}

      <NavigationButtons
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel="Continue to Dissemination"
      />
    </div>
  );
}

export default D9_SpecialConsiderations;
